import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDebounce } from "@uidotdev/usehooks";
import Modal from "../../layouts/Modal";
import { useAuth } from "../../contexts/AuthContext";
import {
  ICreateWorkOrderRequest,
  IWorkOrder,
  ISingleWorkOrderResponse,
  WorkOrderItemType,
  WorkOrderStatus,
} from "../../models/WorkOrder";
import useWorkOrderMutations from "../../data/useWorkOrderMutations";
import { SearchPaletteItem } from "../shared/Inputs/SearchPaletteTypes";
import WorkOrderForm from "./WorkOrderForm";
import AddToWorkOrderChooserModal from "./AddToWorkOrderChooserModal";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import useApiHelper from "../../hooks/useApiHelper";
import apiConfig from "../../utils/apiConfig";
import { IPagedResponse } from "../shared/Tables/PagedTable";
import AssigneePill from "./AssigneePill";

type BulkWorkOrderModalView = "chooser" | "create" | "existingLookup" | "existingEdit";

interface BulkAddToWorkOrderModalProps {
  open: boolean;
  onClose: () => void;
  selectedItems: SearchPaletteItem[];
  /**
   * Fired once the work order has actually saved, for callers that need to do something to the
   * items afterwards. Deliberately hung off the mutation rather than the form's onComplete, which
   * fires on submit - a caller acting on a save that then failed would be acting on a lie.
   */
  onAdded?: () => void;
}

export default function BulkAddToWorkOrderModal({
  open,
  onClose,
  selectedItems,
  onAdded,
}: BulkAddToWorkOrderModalProps) {
  const [view, setView] = useState<BulkWorkOrderModalView>("chooser");
  const [existingSearch, setExistingSearch] = useState("");
  const [selectedExistingWorkOrderId, setSelectedExistingWorkOrderId] = useState<string | null>(null);
  const debouncedSearch = useDebounce(existingSearch, 400);
  const { userId, claims } = useAuth();
  const { create, update } = useWorkOrderMutations();
  const { post, get } = useApiHelper(apiConfig("BulkAddToWorkOrderModal"));

  useEffect(() => {
    if (open) {
      setView("chooser");
      setExistingSearch("");
      setSelectedExistingWorkOrderId(null);
    }
  }, [open]);

  const sanitizedItems = useMemo(
    () =>
      selectedItems.map((item) => ({
        id: item.id,
        name: item.name,
        type: item.type,
      })),
    [selectedItems]
  );

  const workOrderLookup = useQuery({
    queryKey: ["workOrderLookup", debouncedSearch, userId, claims?.isAdmin],
    enabled: open && view === "existingLookup",
    queryFn: async () => {
      const baseBody = {
        id: "created",
        desc: true,
        searchBody: {
          includeCompleted: false,
        } as Record<string, any>,
      };

      const querySuffix = debouncedSearch?.trim() ? `&query=${encodeURIComponent(debouncedSearch.trim())}` : "";
      const endpoint = `/workorder/paginated/1?pageSize=20${querySuffix}`;

      // Same visibility semantics as work-order table:
      // - admins: see all
      // - non-admins: created by me OR assigned to me
      if (claims?.isAdmin) {
        const res = await post<typeof baseBody, IPagedResponse<IWorkOrder>>(endpoint, baseBody);
        return (res.data.rows ?? []).filter((x) => x.status !== WorkOrderStatus.Complete).slice(0, 20);
      }

      const [createdRes, assignedRes] = await Promise.all([
        post<typeof baseBody, IPagedResponse<IWorkOrder>>(endpoint, {
          ...baseBody,
          searchBody: {
            ...baseBody.searchBody,
            createdById: userId,
          },
        }),
        post<typeof baseBody, IPagedResponse<IWorkOrder>>(endpoint, {
          ...baseBody,
          searchBody: {
            ...baseBody.searchBody,
            assigneeId: userId,
          },
        }),
      ]);

      const merged = new Map<string, IWorkOrder>();
      for (const item of createdRes.data.rows ?? []) merged.set(item.id, item);
      for (const item of assignedRes.data.rows ?? []) merged.set(item.id, item);

      return Array.from(merged.values())
        .filter((x) => x.status !== WorkOrderStatus.Complete)
        .sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime())
        .slice(0, 20);
    },
    staleTime: 1000 * 30,
  });

  const selectedWorkOrderQuery = useQuery({
    queryKey: ["workOrder", selectedExistingWorkOrderId],
    enabled: open && view === "existingEdit" && Boolean(selectedExistingWorkOrderId),
    queryFn: async () => {
      return get<ISingleWorkOrderResponse>(`/workorder/${selectedExistingWorkOrderId}`);
    },
    staleTime: 1000 * 30,
  });

  const filteredWorkOrders = useMemo(() => workOrderLookup.data ?? [], [workOrderLookup.data]);
  const selectedExistingWorkOrder: IWorkOrder | null = selectedWorkOrderQuery.data?.data ?? null;

  return (
    <>
      <AddToWorkOrderChooserModal
        open={open && view === "chooser"}
        onClose={onClose}
        onCreateNew={() => setView("create")}
        onAddToExisting={() => setView("existingLookup")}
        selectedCount={selectedItems.length}
      />

      <Modal
        open={open && view === "create"}
        setOpen={(isOpen) => {
          if (!isOpen) onClose();
        }}
        width="max-w-5xl"
      >
        <WorkOrderForm
          createdById={userId ?? null}
          initialLinkedItems={sanitizedItems}
          onCancel={onClose}
          isSubmitting={create.isLoading}
          onSubmit={(request: ICreateWorkOrderRequest) =>
            create.mutate(request, { onSuccess: () => onAdded?.() })
          }
          onComplete={onClose}
        />
      </Modal>

      <Modal
        open={open && view === "existingLookup"}
        setOpen={(isOpen) => {
          if (!isOpen) onClose();
        }}
        width="max-w-4xl"
      >
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Add to existing work order</h2>
          <p className="text-sm text-gray-600">Find and select a work order to add your selected items.</p>

          <input
            type="text"
            value={existingSearch}
            onChange={(e) => setExistingSearch(e.target.value)}
            placeholder="Search by order number, title, or notes..."
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />

          <div className="max-h-72 overflow-auto rounded-md border border-gray-200 divide-y divide-gray-100">
            {workOrderLookup.isLoading && (
              <div className="px-4 py-8 text-sm text-gray-500">Loading work orders...</div>
            )}

            {(filteredWorkOrders ?? []).map((order) => {
              const isSelected = selectedExistingWorkOrderId === order.id;
              const existingKeys = new Set(
                (order.workOrderItems ?? []).map((item) => `${item.linkedId}::${item.type}`)
              );
              const alreadyLinkedCount = sanitizedItems.filter((item) =>
                existingKeys.has(`${String(item.id)}::${mapSearchTypeToWorkOrderItemType(item.type)}`)
              ).length;
              return (
                <button
                  key={order.id}
                  type="button"
                  onClick={() => setSelectedExistingWorkOrderId(order.id)}
                  className={`relative w-full px-4 py-3 text-left hover:bg-gray-50 ${
                    isSelected
                      ? "bg-primary-50 before:content-[''] before:absolute before:left-0 before:top-0 before:h-full before:w-1 before:bg-primary-500"
                      : ""
                  }`}
                >
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{order.orderNumber} - {order.title}</p>
                      <div className="mt-1 flex items-center gap-2">
                        <p className="text-xs text-gray-500">
                          {order.workOrderItems?.length ?? 0} linked item{(order.workOrderItems?.length ?? 0) === 1 ? "" : "s"}
                        </p>
                        {alreadyLinkedCount > 0 && (
                          <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">
                            {alreadyLinkedCount} already on work order
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="text-xs text-gray-500">
                      {formatWorkOrderStatus(order.status)}
                    </span>
                  </div>

                  <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-gray-500">Created</p>
                      <p className="text-sm font-medium text-gray-800">{formatWorkOrderDate(order.created)}</p>
                    </div>
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-gray-500">Due Date</p>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-gray-800">{formatWorkOrderDate(order.dueDate)}</p>
                        {renderDueDateIndicator(order.dueDate)}
                      </div>
                    </div>
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-gray-500">Assigned To</p>
                      <AssigneePill assigneeId={order.assigneeId} />
                    </div>
                  </div>
                </button>
              );
            })}

            {!workOrderLookup.isLoading && filteredWorkOrders.length === 0 && (
              <div className="px-4 py-8 text-sm text-gray-500">No work orders found.</div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <ButtonInput
              label="Back"
              isSubmit={false}
              classes="!bg-gray-200 !text-gray-700 hover:!bg-gray-300"
              onClick={() => setView("chooser")}
            />
            <ButtonInput
              label="Open selected work order"
              isSubmit={false}
              classes=""
              disabled={!selectedExistingWorkOrderId}
              onClick={() => setView("existingEdit")}
            />
          </div>
        </div>
      </Modal>

      <Modal
        open={open && view === "existingEdit"}
        setOpen={(isOpen) => {
          if (!isOpen) onClose();
        }}
        width="max-w-5xl"
      >
        {selectedExistingWorkOrder ? (
          <WorkOrderForm
            workOrder={selectedExistingWorkOrder}
            initialLinkedItems={sanitizedItems}
            createdById={userId ?? null}
            onCancel={onClose}
            isSubmitting={update.isLoading}
            onSubmit={(request: ICreateWorkOrderRequest) => {
              if (!selectedExistingWorkOrder?.id) return;
              update.mutate(
                { workOrderId: selectedExistingWorkOrder.id, request },
                { onSuccess: () => onAdded?.() }
              );
            }}
            onComplete={onClose}
          />
        ) : (
          <div className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">Loading work order...</h2>
            <p className="text-sm text-gray-600">Please wait while we load the selected work order.</p>
          </div>
        )}
      </Modal>
    </>
  );
}

function formatWorkOrderStatus(status: WorkOrderStatus): string {
  switch (status) {
    case WorkOrderStatus.ToDo:
      return "To Do";
    case WorkOrderStatus.InProgress:
      return "In Progress";
    case WorkOrderStatus.Complete:
      return "Complete";
    default:
      return "Unknown";
  }
}

function mapSearchTypeToWorkOrderItemType(type?: SearchPaletteItem["type"]): WorkOrderItemType {
  switch (type) {
    case "Order":
      return WorkOrderItemType.Order;
    case "Vehicle":
      return WorkOrderItemType.Vehicle;
    case "Stock":
      return WorkOrderItemType.Stock;
    default:
      return WorkOrderItemType.Vehicle;
  }
}

function formatWorkOrderDate(dateValue?: string | null): string {
  if (!dateValue) return "-";
  const date = new Date(dateValue);
  if (isNaN(date.getTime())) return "-";

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function renderDueDateIndicator(dateValue?: string | null): JSX.Element | null {
  if (!dateValue) return null;
  const dueDate = new Date(dateValue);
  if (isNaN(dueDate.getTime())) return null;

  const diffMs = dueDate.getTime() - Date.now();
  if (diffMs < 0) {
    return <span className="inline-flex rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">Overdue</span>;
  }

  if (diffMs <= 1000 * 60 * 60 * 24) {
    return <span className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">Due Soon</span>;
  }

  return null;
}
